import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function ReviewsSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal({ threshold: 0.2 });

  return (
    <section id="yorumlar" className="w-full py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-secondary-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className={`text-center mb-10 md:mb-14 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            Misafirlerimizin <span className="text-primary-500">Deneyimleri</span>
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            İlkim Butik Otel'de amacımız, misafirlerimize huzurlu, samimi ve unutulmaz bir tatil deneyimi sunmak.
          </p>
        </div>

        {/* Placeholder Design */}
        <div ref={contentRef} className={`bg-background-50 rounded-3xl p-8 md:p-12 border border-background-200/60 text-center transition-all duration-700 delay-200 ${contentVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-[0.98]'}`}>
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-50 text-primary-400 animate-pulse">
            <i className="ri-message-3-line text-3xl" />
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground-700 mb-2">
            Misafir Yorumları
          </h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto leading-relaxed">
            Misafir yorumları bu alanda gösterilecektir. Deneyimlerini paylaşan misafirlerimizin değerlendirmelerini yakında burada görebilirsiniz.
          </p>
        </div>

        {/* CTA */}
        <div className={`text-center mt-8 md:mt-10 transition-all duration-700 delay-300 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <a
            href="https://wa.me/905395851199?text=Merhaba%2C%20İlkim%20Butik%20Otel%20için%20fiyat%20ve%20müsaitlik%20bilgisi%20almak%20istiyorum.%20Fiyat%20teklifi%20alabilir%20miyim%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-background-50 rounded-xl font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
          >
            <i className="ri-whatsapp-line" />
            Yorumları İnceledim, Fiyat Almak İstiyorum
          </a>
        </div>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: 'Otel denize ne kadar uzaklıkta?',
    a: 'Otelimiz denize yalnızca 70 metre mesafededir.',
  },
  {
    q: "Kız Kumu Plajı'na yakın mı?",
    a: "Evet. Dünyaca ünlü Kız Kumu Plajı'na yürüme mesafesindedir.",
  },
  {
    q: 'Aile odası var mı?',
    a: 'Evet. 1+1 mutfaklı aile odalarımız aileler ve uzun süreli konaklamalar için uygundur.',
  },
  {
    q: 'Standart oda var mı?',
    a: 'Evet. Çiftler, tek veya iki kişilik konaklamalar için standart oda seçeneklerimiz bulunmaktadır.',
  },
  {
    q: 'Kahvaltı var mı?',
    a: 'Evet. Yöresel ürünlerle hazırlanan serpme köy kahvaltısı sunulmaktadır.',
  },
  {
    q: 'Havuz var mı?',
    a: 'Evet. Otelimizde açık yüzme havuzu ve dinlenme alanları bulunmaktadır.',
  },
  {
    q: 'Fiyat bilgisi nasıl alınır?',
    a: 'Giriş tarihi, çıkış tarihi, kişi sayısı ve oda tercihinizi seçerek WhatsApp üzerinden hızlıca fiyat bilgisi alabilirsiniz.',
  },
  {
    q: 'Rezervasyon için ne yapmalıyım?',
    a: 'Sayfadaki "Tarih Seç, Fiyat Teklifi Al" butonuna basarak WhatsApp üzerinden rezervasyon bilgisi alabilirsiniz.',
  },
];

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: listRef, isVisible: listVisible } = useScrollReveal({ threshold: 0.05 });

  return (
    <section id="sss" className="w-full py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className={`text-center mb-10 md:mb-14 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            Rezervasyon Öncesi <span className="text-accent-500">Merak Edilenler</span>
          </h2>
        </div>

        {/* Accordion */}
        <div ref={listRef} className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`bg-background-100 rounded-2xl border border-background-200/60 overflow-hidden transition-all duration-500 hover:border-background-300/80 ${listVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 md:px-6 md:py-5 text-left cursor-pointer hover:bg-background-200/30 transition-colors"
              >
                <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                  {faq.q}
                </span>
                <i
                  className={`ri-${openIndex === i ? 'subtract' : 'add'}-line text-xl text-primary-500 shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`}
                />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-40' : 'max-h-0'}`}>
                <div className="px-5 pb-4 md:px-6 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}